import { AddIcon, Button, Grid, GridRow } from '@bp/ui-components';
import { FieldArray, useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { PersonRole } from '../../../../types/planung-graphql-client-defs';
import { partition } from '../../../../utils/arrayFunc';
import { LessonFormTeacherType, LessonFormType } from '../../types';
import { LessonTeacherFormItem } from './LessonTeacherFormItem';

type LessonTeacherFormProps = { disabled: boolean; defaults?: LessonFormTeacherType };

export const LessonTeacherForm = observer(({ disabled, defaults }: LessonTeacherFormProps) => {
  const { t } = useTranslation();

  const { values, setFieldTouched } = useFormikContext<LessonFormType>();

  const { teacherData } = useLoadBasicData({ pause: false });

  const teachers = teacherData?.people.filter((person) =>
    person.organizationConnection.edges.some((e) => e.properties.name === PersonRole.Teacher),
  );

  const [qualifiedTeachers, other] = partition(
    teachers ?? [],
    (person) => !!person.qualifications.find((quali) => quali.subject.uuid === values.subject?.uuid),
  );

  return (
    <FieldArray name={'teachers'}>
      {(arrayHelpers) => {
        return (
          <Grid useFormGap>
            {values.teachers?.map((teacher, index) => {
              teacher.teachingLoadHoursDisplay = (
                values.teachingLoadInfo?.deputatByTeacher.get(teacher.person?.uuid ?? '') ?? 0
              ).toLocaleString('de');

              return (
                <LessonTeacherFormItem
                  key={teacher.person?.uuid}
                  disabled={disabled}
                  index={index}
                  qualifiedTeachers={qualifiedTeachers}
                  otherTeachers={other}
                  teacher={teacher}
                  onDelete={() => arrayHelpers.remove(index)}
                />
              );
            })}

            <GridRow spacingBottom='none' spacingTop='none'>
              <Button
                hierarchy='tertiary'
                icon={<AddIcon />}
                disabled={!values.subject || disabled}
                onClick={async () => {
                  if (values.teachers && values.teachers.length > 0) {
                    for (const index in values.teachers) {
                      await setFieldTouched(`teachers.${index}`);
                    }
                  }
                  arrayHelpers.push({ person: { uuid: v4() }, ...defaults });
                }}
              >
                {t('persons.addTeacher')}
              </Button>
            </GridRow>
          </Grid>
        );
      }}
    </FieldArray>
  );
});
